import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import RiskBadge from "@/components/RiskBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft,
  Phone,
  Mail,
  AtSign,
  Image as ImageIcon,
  Tag,
  Calendar,
  MapPin,
  ExternalLink,
  Network,
  MessageSquareText,
  Download,
  FolderPlus,
  EyeOff,
  Languages,
} from "lucide-react";

const LANG_META = {
  en: { label: "English", tone: "text-slate-600 bg-slate-50 border-slate-200" },
  kn: { label: "Kannada", tone: "text-amber-800 bg-amber-50 border-amber-200" },
  mixed: { label: "Kannada + English", tone: "text-purple-800 bg-purple-50 border-purple-200" },
  unknown: { label: "Unknown language", tone: "text-slate-500 bg-slate-50 border-slate-200" },
};

function Chip({ icon: Icon, label, to, testid }) {
  const content = (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1.5 border border-slate-200 bg-slate-50 px-2 py-1 rounded-md text-xs font-mono hover:bg-slate-100"
    >
      <Icon className="h-3 w-3 text-slate-500" />
      {label}
    </span>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

export default function AdDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const readOnly = user?.role === "analyst";
  const [ad, setAd] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [provenance, setProvenance] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: adData }, { data: notesData }, provResp] = await Promise.all([
        api.get(`/ads/${id}`),
        api.get(`/ads/${id}/notes`),
        api.get(`/ads/${id}/provenance`).catch(() => ({ data: null })),
      ]);
      setAd(adData);
      setNotes(notesData);
      setProvenance(provResp.data);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!readOnly) api.get("/cases").then(({ data }) => setCases(data)).catch(() => {});
  }, [readOnly]);

  const addToCase = async (caseId) => {
    if (!caseId) return;
    try {
      await api.post(`/cases/${caseId}/ads`, { ad_id: id });
      toast.success("Added to case");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add to case");
    }
  };

  const downloadDossier = async () => {
    try {
      const res = await api.get(`/ads/${id}/dossier`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MUKTI_ad_${id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Dossier downloaded");
    } catch {
      toast.error("Could not generate dossier");
    }
  };

  const submitNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    try {
      await api.post(`/ads/${id}/notes`, { body: noteText });
      setNoteText("");
      toast.success("Case note added");
      fetchAll();
    } catch (err) {
      toast.error("Could not save note");
    }
  };

  const [analyzing, setAnalyzing] = useState(false);
  const runAiAnalysis = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/ads/${id}/analyze`);
      setAd((prev) => ({
        ...prev,
        ai_analysis: data.analysis,
        ai_summary: data.analysis?.summary || "",
        risk: data.risk,
      }));
      toast.success("AI analysis complete");
    } catch (err) {
      toast.error("AI analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const [translating, setTranslating] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const runTranslate = async () => {
    if (ad?.translation_en?.title) {
      setShowTranslation((v) => !v);
      return;
    }
    setTranslating(true);
    try {
      const { data } = await api.post(`/ads/${id}/translate`);
      setAd((prev) => ({ ...prev, translation_en: data.translation }));
      setShowTranslation(true);
      toast.success(data.cached ? "Translation loaded" : "Translated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Translation failed");
    } finally {
      setTranslating(false);
    }
  };

  if (loading || !ad) {
    return <div className="p-6 text-sm text-slate-500">Loading advertisement…</div>;
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-6xl" data-testid="ad-detail-page">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="text-xs text-slate-500 hover:text-slate-900 font-mono uppercase tracking-wider inline-flex items-center gap-1"
      >
        <ArrowLeft className="h-3 w-3" /> Back
      </button>

      <header className="flex flex-col lg:flex-row gap-6 lg:items-end justify-between">
        <div className="space-y-3 min-w-0">
          <div className="text-xs font-mono uppercase tracking-wider text-blue-700">
            Advertisement · {ad.id.slice(0, 8)}
          </div>
          <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl">
            {ad.title}
          </h1>
          <div className="flex flex-wrap gap-2 items-center text-xs text-slate-600">
            {ad.locality && (
              <Chip icon={MapPin} label={ad.locality} testid="ad-locality" />
            )}
            <Chip
              icon={Calendar}
              label={(ad.posted_at || "").slice(0, 10)}
              testid="ad-posted"
            />
            {ad.lang && ad.lang !== "unknown" && (
              <span
                data-testid={`ad-lang-${ad.lang}`}
                className={`inline-flex items-center gap-1.5 border px-2 py-1 rounded-md text-xs font-mono ${(LANG_META[ad.lang] || LANG_META.unknown).tone}`}
                title="Language detected from the ad text"
              >
                <Languages className="h-3 w-3" />
                {(LANG_META[ad.lang] || LANG_META.unknown).label}
              </span>
            )}
            {ad.source_url && (
              <a
                href={ad.source_url}
                target="_blank"
                rel="noreferrer"
                data-testid="ad-source"
                className="inline-flex items-center gap-1 text-blue-700 hover:underline"
              >
                <ExternalLink className="h-3 w-3" /> Source
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!readOnly && (
            <select
              data-testid="add-to-case-select"
              defaultValue=""
              onChange={(e) => { addToCase(e.target.value); e.target.value = ""; }}
              className="text-sm border border-slate-300 rounded-md px-3 py-2 hover:bg-slate-50"
            >
              <option value="" disabled>+ Add to case…</option>
              {cases.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          )}
          <Button
            data-testid="download-ad-dossier"
            onClick={downloadDossier}
            variant="outline"
            className="gap-1.5 border-slate-300"
          >
            <Download className="h-4 w-4" /> Dossier
          </Button>
          <Link
            to={`/graph?focus=${ad.id}`}
            data-testid="open-graph"
            className="inline-flex items-center gap-1.5 text-sm bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 rounded-md"
          >
            <Network className="h-4 w-4" /> Open in graph
          </Link>
          <Link
            to={`/assistant?ad=${ad.id}`}
            data-testid="ask-ai"
            className="inline-flex items-center gap-1.5 text-sm border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md"
          >
            <MessageSquareText className="h-4 w-4" /> Ask MUKTI
          </Link>
        </div>
      </header>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6">
          {provenance && provenance.sources && provenance.sources.length > 1 && (
            <section
              className="border border-slate-200 bg-white rounded-md p-4"
              data-testid="provenance-strip"
            >
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-display font-bold text-sm tracking-tight flex items-center gap-1.5">
                  <ExternalLink className="h-3.5 w-3.5 text-blue-700" /> Seen in
                </h2>
                <span className="text-[10px] font-mono text-slate-400">
                  {provenance.sources.length} sources · {provenance.total_matches} match(es)
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {provenance.sources.map((s) => {
                  const isSelf = (s.matched_via || []).includes("self");
                  return (
                    <div
                      key={s.source_name + s.source_type}
                      data-testid={`provenance-source-${s.source_name.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`inline-flex items-center gap-1.5 border rounded-md px-2 py-1 text-xs ${
                        isSelf
                          ? "bg-blue-50 border-blue-200 text-blue-800"
                          : "bg-slate-50 border-slate-200 text-slate-700"
                      }`}
                      title={
                        (s.first_seen ? `First seen ${s.first_seen.slice(0, 10)}` : "") +
                        (s.matched_via?.length ? ` · via ${s.matched_via.join("+")}` : "")
                      }
                    >
                      <span className="font-medium">{s.source_name}</span>
                      <span className="text-[10px] font-mono text-slate-500">
                        {s.ad_count} ad{s.ad_count > 1 ? "s" : ""}
                      </span>
                      {!isSelf && s.matched_via?.length > 0 && (
                        <span className="text-[10px] font-mono text-amber-700">
                          via {s.matched_via.join("+")}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section className="border border-slate-200 bg-white rounded-md p-5" data-testid="ad-text-section">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h2 className="font-display font-bold text-lg tracking-tight">
                Advertisement text
              </h2>
              {ad.lang && ad.lang !== "en" && ad.lang !== "unknown" && (
                <Button
                  onClick={runTranslate}
                  disabled={translating}
                  size="sm"
                  variant="outline"
                  data-testid="translate-btn"
                  className="gap-1.5 border-slate-300"
                >
                  <Languages className="h-3.5 w-3.5" />
                  {translating
                    ? "Translating…"
                    : showTranslation
                      ? "Hide translation"
                      : ad.translation_en?.title
                        ? "Show English translation"
                        : "Translate to English"}
                </Button>
              )}
            </div>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="ad-text-original">
              {ad.text}
            </p>
            {showTranslation && ad.translation_en?.text && (
              <div
                data-testid="ad-text-translation"
                className="mt-4 border-l-2 border-blue-400 bg-blue-50/40 pl-4 py-3 rounded-r-md"
              >
                <div className="text-[10px] uppercase tracking-wider text-blue-700 font-mono mb-1 flex items-center gap-1.5">
                  <Languages className="h-3 w-3" /> English translation · AI-assisted
                </div>
                {ad.translation_en.title && (
                  <div className="text-sm font-medium text-slate-800 mb-1">
                    {ad.translation_en.title}
                  </div>
                )}
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {ad.translation_en.text}
                </p>
              </div>
            )}
          </section>

          {ad.image_url && (
            <section className="border border-slate-200 bg-white rounded-md p-5">
              <h2 className="font-display font-bold text-lg tracking-tight mb-3">
                Image
              </h2>
              <img
                src={ad.image_url}
                alt="ad"
                className="max-h-80 rounded-md border border-slate-200 object-cover"
              />
            </section>
          )}

          <section className="border border-slate-200 bg-white rounded-md p-5">
            <h2 className="font-display font-bold text-lg tracking-tight mb-3">
              Extracted entities
            </h2>
            {(ad.obfuscation || []).length > 0 && (
              <div data-testid="obfuscation-banner" className="flex flex-wrap items-center gap-2 mb-4 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                <EyeOff className="h-4 w-4 text-amber-700 shrink-0" />
                <span className="text-xs text-amber-800 font-medium">Contact obfuscation detected:</span>
                {ad.obfuscation.map((t) => (
                  <span key={t} data-testid={`obfuscation-${t}`} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white border border-amber-300 text-amber-800">
                    {t.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-2">
                  Phones
                </div>
                <div className="flex flex-wrap gap-2">
                  {(ad.phones || []).map((p) => (
                    <Chip
                      key={p}
                      icon={Phone}
                      label={p}
                      to={`/lookup/phone?q=${encodeURIComponent(p)}`}
                      testid={`phone-${p}`}
                    />
                  ))}
                  {(ad.partial_phones || []).map((p) => (
                    <span key={p} data-testid={`partial-phone-${p}`} title="Masked number recovered from the ad text — '?' marks hidden digits"
                      className="inline-flex items-center gap-1.5 text-xs font-mono px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800">
                      <EyeOff className="h-3 w-3" /> {p}
                    </span>
                  ))}
                  {(ad.phones || []).length === 0 && (ad.partial_phones || []).length === 0 && (
                    <span className="text-xs text-slate-400">None</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-2">
                  Emails
                </div>
                <div className="flex flex-wrap gap-2">
                  {(ad.emails || []).map((e) => (
                    <Chip key={e} icon={Mail} label={e} testid={`email-${e}`} />
                  ))}
                  {(ad.emails || []).length === 0 && (
                    <span className="text-xs text-slate-400">None</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-2">
                  Usernames
                </div>
                <div className="flex flex-wrap gap-2">
                  {(ad.usernames || []).map((u) => (
                    <Chip key={u} icon={AtSign} label={u} testid={`username-${u}`} />
                  ))}
                  {(ad.usernames || []).length === 0 && (
                    <span className="text-xs text-slate-400">None</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-2">
                  Image hashes
                </div>
                <div className="flex flex-wrap gap-2">
                  {(ad.image_hashes || []).map((h) => (
                    <Chip
                      key={h}
                      icon={ImageIcon}
                      label={h.slice(0, 14) + "…"}
                      to={`/lookup/image?q=${encodeURIComponent(h)}`}
                      testid={`image-${h}`}
                    />
                  ))}
                  {(ad.image_hashes || []).length === 0 && (
                    <span className="text-xs text-slate-400">None</span>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-2">
                  Suspicious keywords
                </div>
                <div className="flex flex-wrap gap-2">
                  {(ad.keywords || []).map((k) => (
                    <Chip key={k} icon={Tag} label={k} testid={`kw-${k}`} />
                  ))}
                  {(ad.keywords || []).length === 0 && (
                    <span className="text-xs text-slate-400">None</span>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="border border-slate-200 bg-white rounded-md p-5" data-testid="ai-analysis-section">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="font-display font-bold text-lg tracking-tight">
                AI narrative analysis
              </h2>
              {!readOnly && (
                <Button
                  onClick={runAiAnalysis}
                  disabled={analyzing}
                  size="sm"
                  data-testid="run-ai-analysis"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {analyzing ? "Analysing…" : ad.ai_analysis ? "Re-run analysis" : "Run AI analysis"}
                </Button>
              )}
            </div>
            {ad.ai_analysis ? (
              <div className="space-y-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-1">Summary</div>
                  <div className="text-sm text-slate-800">{ad.ai_analysis.summary || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-1">Intent flags</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(ad.ai_analysis.intent_flags || []).map((f) => (
                      <span key={f} data-testid={`intent-${f}`}
                        className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-200">
                        {f.replace(/_/g, " ")}
                      </span>
                    ))}
                    {(ad.ai_analysis.intent_flags || []).length === 0 && (
                      <span className="text-xs text-slate-400">None flagged</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-1">
                    Rationale · narrative +{ad.ai_analysis.narrative_score || 0}
                  </div>
                  <div className="text-sm text-slate-700 italic">{ad.ai_analysis.rationale || "—"}</div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500">
                Not analysed yet. Run the LLM classifier to extract a neutral summary, intent flags, and a narrative risk contribution.
              </div>
            )}
          </section>

          <section className="border border-slate-200 bg-white rounded-md p-5">
            <h2 className="font-display font-bold text-lg tracking-tight mb-3">
              Similar advertisements
            </h2>
            <div className="divide-y divide-slate-100">
              {(ad.similar || []).map((s) => (
                <Link
                  key={s.id}
                  to={`/ads/${s.id}`}
                  data-testid={`similar-${s.id}`}
                  className="flex items-center justify-between py-2 hover:bg-slate-50 -mx-2 px-2 rounded-md"
                >
                  <div className="min-w-0">
                    <div className="text-sm truncate">{s.title}</div>
                    <div className="text-xs text-slate-500 font-mono truncate">
                      {(s.phones || []).join(" · ") || "no shared phone"}
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 font-mono">
                    {s.locality}
                  </span>
                </Link>
              ))}
              {(ad.similar || []).length === 0 && (
                <div className="text-xs text-slate-400 py-2">
                  No overlapping signals found.
                </div>
              )}
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <section className="border border-slate-200 bg-white rounded-md p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold text-lg tracking-tight">
                Risk score
              </h2>
              <RiskBadge level={ad.risk.level} score={ad.risk.score} />
            </div>
            <div className="text-xs text-slate-500 mb-3">
              Explainable reasoning
            </div>
            <ul className="space-y-3">
              {(ad.risk.reasons || []).map((r, idx) => (
                <li
                  key={`${r.code}-${idx}`}
                  className="border-l-2 border-slate-200 pl-3 text-sm"
                  data-testid={`reason-${r.code}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{r.label}</span>
                    <span className="text-xs font-mono text-slate-500">
                      +{r.points}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500">{r.detail}</div>
                </li>
              ))}
              {(ad.risk.reasons || []).length === 0 && (
                <li className="text-xs text-slate-400">
                  No risk signals detected.
                </li>
              )}
            </ul>
          </section>

          <section className="border border-slate-200 bg-white rounded-md p-5">
            <h2 className="font-display font-bold text-lg tracking-tight mb-3">
              Case notes
            </h2>
            <form onSubmit={submitNote} className="space-y-2 mb-4">
              <Textarea
                data-testid="note-input"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add an investigative note…"
                rows={3}
                disabled={readOnly}
              />
              <Button
                data-testid="note-submit"
                type="submit"
                size="sm"
                disabled={readOnly}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Add note
              </Button>
            </form>
            <ul className="space-y-3">
              {notes.map((n) => (
                <li
                  key={n.id}
                  className="border border-slate-100 rounded-md p-3 bg-slate-50"
                >
                  <div className="text-xs text-slate-500 font-mono mb-1">
                    {n.author_name} · {(n.created_at || "").slice(0, 16).replace("T", " ")}
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{n.body}</div>
                </li>
              ))}
              {notes.length === 0 && (
                <li className="text-xs text-slate-400">No notes yet.</li>
              )}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}
